import ExcelJS from 'exceljs';

/**
 * ENTERING/LEAVING AIR STATUS 섹션을 엑셀 시트에 추가
 * @param worksheet 엑셀 워크시트
 * @param ahu AHU 데이터
 * @param currentRow 시작 행 번호
 * @param STYLES 스타일 객체
 * @returns 다음 행 번호
 */
export const addEnteringLeavingAirStatus = (
  worksheet: ExcelJS.Worksheet,
  ahu: any,
  currentRow: number,
  STYLES: any
): number => {
  let row: ExcelJS.Row;

  const fanData = ahu.fanSelectionData;
  const supplyFan = fanData?.supplyFan || {};
  const returnFan = fanData?.returnFan || {};
  const calc = ahu.calculationResults || {};

  // 병합된 셀에 스타일 적용하는 헬퍼 함수
  const applyMergedCellStyle = (ws: ExcelJS.Worksheet, range: string, style: any) => {
    const [start, end] = range.split(':');

    if (!end) {
      // 단일 셀인 경우
      ws.getCell(start).style = style;
      return;
    }

    // 범위 파싱 (예: A10:B10)
    const startCol = start.match(/[A-Z]+/)?.[0] || '';
    const startRow = parseInt(start.match(/\d+/)?.[0] || '0');
    const endCol = end.match(/[A-Z]+/)?.[0] || '';
    const endRow = parseInt(end.match(/\d+/)?.[0] || '0');

    // 열 문자를 숫자로 변환
    const colToNum = (col: string): number => {
      let num = 0;
      for (let i = 0; i < col.length; i++) {
        num = num * 26 + (col.charCodeAt(i) - 64);
      }
      return num;
    };

    const startColNum = colToNum(startCol);
    const endColNum = colToNum(endCol);

    // 병합된 모든 셀에 스타일 적용
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startColNum; col <= endColNum; col++) {
        ws.getRow(row).getCell(col).style = style;
      }
    }
  };

  // 값 검증 함수
  const sanitizeCellValue = (value: any): string | number => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && !isNaN(value)) return value;
    return String(value);
  };

  // ============================================
  // ENTERING/LEAVING AIR STATUS 섹션
  // ============================================
  // 기존 병합이 있을 수 있으므로 먼저 해제
  currentRow+=2;
  worksheet.unMergeCells(`A${currentRow}:M${currentRow}`);

  worksheet.mergeCells(`A${currentRow}:M${currentRow}`);

 
  const airStatusTitleCell = worksheet.getCell(`A${currentRow}`);
  airStatusTitleCell.value = '■ ENTERING/LEAVING AIR STATUS';
  airStatusTitleCell.style = {
    font: { name: '맑은 고딕', size: 11, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' }
  };
  currentRow++;

  // OA' Cond. - Cooling
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = "OA' Cond. -";
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  row.getCell('C').value = 'Cooling';
  row.getCell('C').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined } };

  const oaCoolTemp = calc.outdoorAir?.dryBulbTemp?.toFixed(1) || '-';
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = oaCoolTemp !== '-' ? `${oaCoolTemp} ℃ DB` : '-';
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const oaCoolRH = calc.outdoorAir?.relativeHumidity?.toFixed(1) || '-';
  worksheet.mergeCells(`F${currentRow}:G${currentRow}`);
  row.getCell('F').value = oaCoolRH !== '-' ? `${oaCoolRH} % RH` : '-';
  applyMergedCellStyle(worksheet, `F${currentRow}:G${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const oaCoolHumidity = calc.outdoorAir?.humidityRatio?.toFixed(4) || '-';
  worksheet.mergeCells(`H${currentRow}:I${currentRow}`);
  row.getCell('H').value = oaCoolHumidity !== '-' ? `${oaCoolHumidity} kg/kg'` : '-';
  applyMergedCellStyle(worksheet, `H${currentRow}:I${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const oaCoolEnthalpy = calc.outdoorAir?.enthalpy?.toFixed(2) || '-';
  worksheet.mergeCells(`J${currentRow}:K${currentRow}`);
  row.getCell('J').value = oaCoolEnthalpy !== '-' ? `${oaCoolEnthalpy} kJ/kg` : '-';
  applyMergedCellStyle(worksheet, `J${currentRow}:K${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  // K열 다음 빈 셀들 (L, M)에도 경계선 적용
  row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
  currentRow++;

  // OA' Cond. - Heating
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  row.getCell('C').value = 'Heating';
  row.getCell('C').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined } };

  const oaHeatTemp = calc.heatingOutdoorAir?.dryBulbTemp?.toFixed(1) || '-';
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = oaHeatTemp !== '-' ? `${oaHeatTemp} ℃ DB` : '-';
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const oaHeatRH = calc.heatingOutdoorAir?.relativeHumidity?.toFixed(1) || '-';
  worksheet.mergeCells(`F${currentRow}:G${currentRow}`);
  row.getCell('F').value = oaHeatRH !== '-' ? `${oaHeatRH} % RH` : '-';
  applyMergedCellStyle(worksheet, `F${currentRow}:G${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const oaHeatHumidity = calc.heatingOutdoorAir?.humidityRatio?.toFixed(4) || '-';
  worksheet.mergeCells(`H${currentRow}:I${currentRow}`);
  row.getCell('H').value = oaHeatHumidity !== '-' ? `${oaHeatHumidity} kg/kg'` : '-';
  applyMergedCellStyle(worksheet, `H${currentRow}:I${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const oaHeatEnthalpy = calc.heatingOutdoorAir?.enthalpy?.toFixed(2) || '-';
  worksheet.mergeCells(`J${currentRow}:K${currentRow}`);
  row.getCell('J').value = oaHeatEnthalpy !== '-' ? `${oaHeatEnthalpy} kJ/kg` : '-';
  applyMergedCellStyle(worksheet, `J${currentRow}:K${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  // K열 다음 빈 셀들 (L, M)에도 경계선 적용
  row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
  currentRow++;

  // ● Cooling Coil Part 헤더
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:M${currentRow}`);
  row.getCell('A').value = '● Cooling Coil Part';
  applyMergedCellStyle(worksheet, `A${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL_BOLD, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } } as ExcelJS.FillPattern });
  currentRow++;

  // SA / RA Fan Load 계산
  const saFanRatio = (calc?.sfLoadPercentage || 0) / 100;
  const sfAirflow = supplyFan?.airflow || 0;
  const sfStaticPressure = supplyFan?.staticPressure || 0;
  const sfEfficiency = (supplyFan?.efficiency || 65) / 100;
  const sfSafetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;
  const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

  let sfLoad = 0;
  let sfLoadFormula = '';
  if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
    sfLoad = Math.round((sfAirflow * sfStaticPressure / (3600 * sfEfficiency) * sfSafetyFactor) * saFanRatio);
    sfLoadFormula = `SF Load(Q2) = (${sfAirflow.toLocaleString()} × ${sfStaticPressure} / (3600 × ${sfEfficiency.toFixed(2)}) × ${sfSafetyFactor.toFixed(2)}) x ${saFanRatio.toFixed(2)} =`;
  } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
    sfLoad = Math.round((sfAirflow * sfStaticPressure / (3600 * sfEfficiency)) * saFanRatio);
    sfLoadFormula = `SF Load(Q2) = (${sfAirflow.toLocaleString()} × ${sfStaticPressure} / (3600 × ${sfEfficiency.toFixed(2)})) x ${saFanRatio.toFixed(2)} =`;
  }

  // SA / RA Fan Load - SF Load
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = 'SA / RA Fan Load';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  row.getCell('C').value = sfLoadFormula;
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  row.getCell('K').value = sfLoad > 0 ? `${sfLoad} W` : '';
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // RF Load 계산
  const rfLoadPercentage = calc?.rfLoadPercentage || 0;
  const raFanRatio = rfLoadPercentage / 100;
  const raFanPosition = fanData?.criteria?.raFanPosition || 'Inside';
  const rfAirflow = returnFan?.airflow || 0;
  const rfStaticPressure = returnFan?.staticPressure || 0;
  const rfEfficiency = (returnFan?.efficiency || 50) / 100;
  const rfSafetyFactor = 1 + (returnFan?.safetyFactor || 15) / 100;

  let rfLoad = 0;
  let rfLoadFormula = '';
  if (raFanPosition === 'None') {
    rfLoadFormula = 'RF Load (Q3) =';
  } else if (raFanPosition === 'Inside') {
    rfLoad = Math.round((rfAirflow * rfStaticPressure / (3600 * rfEfficiency) * rfSafetyFactor) * raFanRatio);
    rfLoadFormula = `RF Load(Q3) = (${rfAirflow.toLocaleString()} × ${rfStaticPressure} / (3600 × ${rfEfficiency.toFixed(2)}) × ${rfSafetyFactor.toFixed(2)}) x ${raFanRatio.toFixed(2)} =`;
  } else if (raFanPosition === 'Outside') {
    rfLoad = Math.round((rfAirflow * rfStaticPressure / (3600 * rfEfficiency)) * raFanRatio);
    rfLoadFormula = `RF Load(Q3) = (${rfAirflow.toLocaleString()} × ${rfStaticPressure} / (3600 × ${rfEfficiency.toFixed(2)})) x ${raFanRatio.toFixed(2)} =`;
  }

  // SA / RA Fan Load - RF Load
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  row.getCell('C').value = rfLoadFormula;
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  row.getCell('K').value = rfLoad > 0 ? `${rfLoad} W` : '0';
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // RA' Condition
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = "RA' Condition";
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  const raTempValue = calc.returnAir?.dryBulbTemp?.toFixed(1) || '-';
  worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
  row.getCell('C').value = raTempValue !== '-' ? `${raTempValue} ℃ DB` : '-';
  applyMergedCellStyle(worksheet, `C${currentRow}:D${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined }
  });

  const raRH = calc.returnAir?.relativeHumidity?.toFixed(1) || '-';
  worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
  row.getCell('E').value = raRH !== '-' ? `${raRH} % RH` : '-';
  applyMergedCellStyle(worksheet, `E${currentRow}:F${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const raHumidity = calc.returnAir?.humidityRatio?.toFixed(4) || '-';
  worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
  row.getCell('G').value = raHumidity !== '-' ? `${raHumidity} kg/kg'` : '-';
  applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const raEnthalpy = calc.returnAir?.enthalpy?.toFixed(2) || '-';
  worksheet.mergeCells(`I${currentRow}:J${currentRow}`);
  row.getCell('I').value = raEnthalpy !== '-' ? `${raEnthalpy} kJ/kg` : '-';
  applyMergedCellStyle(worksheet, `I${currentRow}:J${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  // J열 다음 빈 셀들 (K, L, M)에도 경계선 적용
  row.getCell('K').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
  currentRow++;

  // RA Load 계산
  // Q1 = 천장속(플레넘) 조명에 의한 RA 온도상승 부하 (Cceil)
  // VBA: RAlo = SFte + RFte + Cceil 에서 Cceil에 해당
  // '풍량 선정' 탭의 "환기 조명부하(W)" 입력값(= inputs.ventilationLightingLoad)이
  // AHUCalculator.calculateFanLoads()에서 calc.ceilingLightingLoad로 저장됨
  const q1 = calc.ceilingLightingLoad || 0;
  const raLoad = saFanPosition.startsWith('Blow') ? Math.round(q1) + sfLoad + rfLoad : Math.round(q1) + rfLoad;
  const raLoadFormula = saFanPosition.startsWith('Blow')
    ? `RA Load = Q1 + Q2(Blow Type) + Q3 = ${Math.round(q1)} + ${sfLoad} + ${rfLoad} =`
    : `RA Load = Q1 + Q3 = ${Math.round(q1)} + ${rfLoad} =`;

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  row.getCell('C').value = raLoadFormula;
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  row.getCell('K').value = `${raLoad} W`;
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // RA Temp 계산
  const raAirflow = returnFan?.airflow || 0;
  const raTemp = raAirflow > 0 ? raLoad / (0.335 * raAirflow) : 0;

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  row.getCell('C').value = `RA Temp. = ${raLoad.toLocaleString()} W ÷ (0.335 × ${raAirflow.toLocaleString()} ㎥/h) = ${raTemp.toFixed(2)} →`;
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  row.getCell('K').value = `${raTemp.toFixed(1)} ℃`;
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // Mixing Air Condition
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = 'Mixing Air Condition';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  const maTempValue = calc.mixedAir?.dryBulbTemp?.toFixed(1) || '-';
  worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
  row.getCell('C').value = maTempValue !== '-' ? `${maTempValue} ℃ DB` : '-';
  applyMergedCellStyle(worksheet, `C${currentRow}:D${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined }
  });

  const maRH = calc.mixedAir?.relativeHumidity?.toFixed(1) || '-';
  worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
  row.getCell('E').value = maRH !== '-' ? `${maRH} % RH` : '-';
  applyMergedCellStyle(worksheet, `E${currentRow}:F${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const maHumidity = calc.mixedAir?.humidityRatio?.toFixed(4) || '-';
  worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
  row.getCell('G').value = maHumidity !== '-' ? `${maHumidity} kg/kg'` : '-';
  applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const maEnthalpy = calc.mixedAir?.enthalpy?.toFixed(2) || '-';
  worksheet.mergeCells(`I${currentRow}:J${currentRow}`);
  row.getCell('I').value = maEnthalpy !== '-' ? `${maEnthalpy} kJ/kg` : '-';
  applyMergedCellStyle(worksheet, `I${currentRow}:J${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  // J열 다음 빈 셀들 (K, L, M)에도 경계선 적용
  row.getCell('K').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
  currentRow++;

  // MAtemp 계산
  const oaAirflow = calc.outdoorAirflow || 0;
  const oaRatio = sfAirflow > 0 ? oaAirflow / sfAirflow : 0;
  const raRatio = 1 - oaRatio;
  const oaTemp = calc.outdoorAir?.dryBulbTemp || 0;
  const raReturnTemp = calc.returnAir?.dryBulbTemp || 0;

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  row.getCell('C').value = `MAtemp = ${oaRatio.toFixed(2)} × ${oaTemp.toFixed(1)} ℃ + ${raRatio.toFixed(2)} × ${raReturnTemp.toFixed(1)} ℃ = ${(oaRatio * oaTemp + raRatio * raReturnTemp).toFixed(2)} →`;
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  const maTempResult = calc.mixedAir?.dryBulbTemp?.toFixed(1) || '-';
  row.getCell('K').value = maTempResult !== '-' ? `${maTempResult} ℃ DB` : '-';
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // MAabso 계산
  const oaAbso = calc.outdoorAir?.humidityRatio || 0;
  const raAbso = calc.returnAir?.humidityRatio || 0;

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  row.getCell('C').value = `MAabso = ${oaRatio.toFixed(2)} × ${oaAbso.toFixed(4)} kg/kg' + ${raRatio.toFixed(2)} × ${raAbso.toFixed(4)} kg/kg' = ${(oaRatio * oaAbso + raRatio * raAbso).toFixed(5)} →`;
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  const maAbso = calc.mixedAir?.humidityRatio?.toFixed(4) || '-';
  row.getCell('K').value = maAbso !== '-' ? `${maAbso} kg/kg'` : '-';
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // Leav'g Air Condition (l1 냉각후)
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = "Leav'g Air Condition";
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  // l1(냉각후) 상태점 값 사용
  const l1Point = ahu.psychroChartData?.points?.find((p: any) =>
    p.name === 'l1' || p.displayName?.includes('냉방코일출구')
  );

  const l1DryBulb = l1Point?.dryBulbTemp ?? calc.supplyAir?.dryBulbTemp;
  const l1RH = l1Point?.relativeHumidity ?? calc.supplyAir?.relativeHumidity;
  const l1Humidity = l1Point?.humidityRatio ?? calc.supplyAir?.humidityRatio;
  const l1Enthalpy = l1Point?.enthalpy ?? calc.supplyAir?.enthalpy;

  const l1TempValue = l1DryBulb?.toFixed(1) || '-';
  worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
  row.getCell('C').value = l1TempValue !== '-' ? `${l1TempValue} ℃ DB` : '-';
  applyMergedCellStyle(worksheet, `C${currentRow}:D${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined }
  });

  const l1RHValue = l1RH?.toFixed(1) || '-';
  worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
  row.getCell('E').value = l1RHValue !== '-' ? `${l1RHValue} % RH` : '-';
  applyMergedCellStyle(worksheet, `E${currentRow}:F${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const l1HumidityValue = l1Humidity?.toFixed(4) || '-';
  worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
  row.getCell('G').value = l1HumidityValue !== '-' ? `${l1HumidityValue} kg/kg'` : '-';
  applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  const l1EnthalpyValue = l1Enthalpy?.toFixed(2) || '-';
  worksheet.mergeCells(`I${currentRow}:J${currentRow}`);
  row.getCell('I').value = l1EnthalpyValue !== '-' ? `${l1EnthalpyValue} kJ/kg` : '-';
  applyMergedCellStyle(worksheet, `I${currentRow}:J${currentRow}`, {
    ...STYLES.TABLE_CELL,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
  });

  // J열 다음 빈 셀들 (K, L, M)에도 경계선 적용
  row.getCell('K').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
  row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
  currentRow++;

  // SA Temp 계산
  const saTemp = sfAirflow > 0 ? sfLoad / (0.335 * sfAirflow) : 0;

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  row.getCell('C').value = `SA Temp. = ${sfLoad.toLocaleString()} W ÷ (0.335 × ${sfAirflow.toLocaleString()} ㎥/h) = ${saTemp.toFixed(2)} →`;
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  const saTemp2 = calc.supplyFanTempRise?.toFixed(1) || '-';
  row.getCell('K').value = saTemp2 !== '-' ? `${saTemp2} ℃` : '-';
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // R/H Temp
  const rhReheatTemp = parseFloat(ahu.formData?.rhReheatTemp) || 0;

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  row.getCell('A').value = '';
  applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
  if (rhReheatTemp === 0) {
    row.getCell('C').value = 'R/H Temp. = (재열 없음)';
  } else {
    row.getCell('C').value = `R/H Temp. = ${rhReheatTemp.toFixed(1)} →`;
  }
  applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
  row.getCell('K').value = rhReheatTemp > 0 ? `${rhReheatTemp.toFixed(1)} ℃` : '-';
  applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
  currentRow++;

  // ● Heating Coil Part (조건부)
  if (calc.heatingMixedAir) {
    row = worksheet.getRow(currentRow);
    worksheet.mergeCells(`A${currentRow}:M${currentRow}`);
    row.getCell('A').value = '● Heating Coil Part';
    applyMergedCellStyle(worksheet, `A${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL_BOLD, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } } as ExcelJS.FillPattern });
    currentRow++;

    // Mixing Air Condition (난방)
    row = worksheet.getRow(currentRow);
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    row.getCell('A').value = 'Mixing Air Condition';
    applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

    const hMaTemp = calc.heatingMixedAir?.dryBulbTemp?.toFixed(1) || '-';
    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    row.getCell('C').value = hMaTemp !== '-' ? `${hMaTemp} ℃ DB` : '-';
    applyMergedCellStyle(worksheet, `C${currentRow}:D${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined }
    });

    const hMaRH = calc.heatingMixedAir?.relativeHumidity?.toFixed(1) || '-';
    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
    row.getCell('E').value = hMaRH !== '-' ? `${hMaRH} % RH` : '-';
    applyMergedCellStyle(worksheet, `E${currentRow}:F${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
    });

    const hMaHumidity = calc.heatingMixedAir?.humidityRatio?.toFixed(4) || '-';
    worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
    row.getCell('G').value = hMaHumidity !== '-' ? `${hMaHumidity} kg/kg'` : '-';
    applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
    });

    const hMaEnthalpy = calc.heatingMixedAir?.enthalpy?.toFixed(2) || '-';
    worksheet.mergeCells(`I${currentRow}:J${currentRow}`);
    row.getCell('I').value = hMaEnthalpy !== '-' ? `${hMaEnthalpy} kJ/kg` : '-';
    applyMergedCellStyle(worksheet, `I${currentRow}:J${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
    });

    // J열 다음 빈 셀들 (K, L, M)에도 경계선 적용
    row.getCell('K').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
    row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
    row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
    currentRow++;

    // MAtemp (난방)
    const heatingOaTemp = calc.heatingOutdoorAir?.dryBulbTemp || 0;
    const heatingRaTemp = calc.heatingReturnAir?.dryBulbTemp || 0;

    row = worksheet.getRow(currentRow);
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    row.getCell('A').value = '';
    applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

    worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
    row.getCell('C').value = `MAtemp = ${oaRatio.toFixed(2)} × ${heatingOaTemp.toFixed(1)} ℃ + ${raRatio.toFixed(2)} × ${heatingRaTemp.toFixed(1)} ℃ = ${(oaRatio * heatingOaTemp + raRatio * heatingRaTemp).toFixed(2)} →`;
    applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

    worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
    const hMaTempResult = calc.heatingMixedAir?.dryBulbTemp?.toFixed(1) || '-';
    row.getCell('K').value = hMaTempResult !== '-' ? `${hMaTempResult} ℃ DB` : '-';
    applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
    currentRow++;

    // MAabso (난방)
    const heatingOaAbso = calc.heatingOutdoorAir?.humidityRatio || 0;
    const heatingRaAbso = calc.heatingReturnAir?.humidityRatio || 0;

    row = worksheet.getRow(currentRow);
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    row.getCell('A').value = '';
    applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

    worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
    row.getCell('C').value = `MAabso = ${oaRatio.toFixed(2)} × ${heatingOaAbso.toFixed(4)} kg/kg' + ${raRatio.toFixed(2)} × ${heatingRaAbso.toFixed(4)} kg/kg' = ${(oaRatio * heatingOaAbso + raRatio * heatingRaAbso).toFixed(5)} →`;
    applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

    worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
    const hMaAbso = calc.heatingMixedAir?.humidityRatio?.toFixed(4) || '-';
    row.getCell('K').value = hMaAbso !== '-' ? `${hMaAbso} kg/kg'` : '-';
    applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
    currentRow++;

    // P/H Coil Air Cond.
    row = worksheet.getRow(currentRow);
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    row.getCell('A').value = 'P/H Coil Air Cond.';
    applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

    const phTemp = calc.heatingSupplyAir?.dryBulbTemp?.toFixed(1) || '-';
    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    row.getCell('C').value = phTemp !== '-' ? `${phTemp} ℃ DB` : '-';
    applyMergedCellStyle(worksheet, `C${currentRow}:D${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined }
    });

    const phRH = calc.heatingSupplyAir?.relativeHumidity?.toFixed(1) || '-';
    worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
    row.getCell('E').value = phRH !== '-' ? `${phRH} % RH` : '-';
    applyMergedCellStyle(worksheet, `E${currentRow}:F${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
    });

    const phHumidity = calc.heatingSupplyAir?.humidityRatio?.toFixed(4) || '-';
    worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
    row.getCell('G').value = phHumidity !== '-' ? `${phHumidity} kg/kg'` : '-';
    applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
    });

    const phEnthalpy = calc.heatingSupplyAir?.enthalpy?.toFixed(2) || '-';
    worksheet.mergeCells(`I${currentRow}:J${currentRow}`);
    row.getCell('I').value = phEnthalpy !== '-' ? `${phEnthalpy} kJ/kg` : '-';
    applyMergedCellStyle(worksheet, `I${currentRow}:J${currentRow}`, {
      ...STYLES.TABLE_CELL,
      alignment: { vertical: 'middle', horizontal: 'right' },
      border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined }
    });

    // J열 다음 빈 셀들 (K, L, M)에도 경계선 적용
    row.getCell('K').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
    row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
    row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
    currentRow++;

    // A/H Coil Air Cond. (조건부)
    if (calc.heatingAfterHeating) {
      row = worksheet.getRow(currentRow);
      worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
      row.getCell('A').value = 'A/H Coil Air Cond.';
      applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

      row.getCell('C').value = 'E.A.T :';
      row.getCell('C').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: STYLES.TABLE_CELL.border?.left, right: undefined } };

      row.getCell('D').value = `${calc.heatingMixedAir?.dryBulbTemp?.toFixed(1) || '-'} ℃ DB`;
      row.getCell('D').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };

      row.getCell('E').value = 'L.A.T :';
      row.getCell('E').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };

      row.getCell('F').value = `${calc.heatingAfterHeating?.dryBulbTemp?.toFixed(1) || '-'} ℃ DB`;
      row.getCell('F').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };

      // G~M 열에 빈 셀 스타일 적용
      row.getCell('G').value = '';
      row.getCell('G').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
      row.getCell('H').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
      row.getCell('I').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
      row.getCell('J').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
      row.getCell('K').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
      row.getCell('L').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: undefined } };
      row.getCell('M').style = { ...STYLES.TABLE_CELL, border: { top: STYLES.TABLE_CELL.border?.top, bottom: STYLES.TABLE_CELL.border?.bottom, left: undefined, right: STYLES.TABLE_CELL.border?.right } };
      currentRow++;
    }

    // LA Temp 계산
    const heatingLoad = parseFloat(ahu.formData?.heatingLoad) || calc.heatingLoad || 0;
    const heatingRaReturnTemp = calc.heatingReturnAir?.dryBulbTemp || 0;

    row = worksheet.getRow(currentRow);
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    row.getCell('A').value = '';
    applyMergedCellStyle(worksheet, `A${currentRow}:B${currentRow}`, { ...STYLES.TABLE_CELL, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } } as ExcelJS.FillPattern });

    worksheet.mergeCells(`C${currentRow}:J${currentRow}`);
    row.getCell('C').value = `LA Temp. = ${heatingLoad.toLocaleString()} W ÷ (0.335 × ${sfAirflow.toLocaleString()} ㎥/h) + ${heatingRaReturnTemp?.toFixed(1) || 0} ℃ =`;
    applyMergedCellStyle(worksheet, `C${currentRow}:J${currentRow}`, STYLES.TABLE_CELL);

    worksheet.mergeCells(`K${currentRow}:M${currentRow}`);
    const laTemp = calc.heatingSupplyAir?.dryBulbTemp?.toFixed(1) || '-';
    row.getCell('K').value = laTemp !== '-' ? `${laTemp} ℃ DB` : '-';
    applyMergedCellStyle(worksheet, `K${currentRow}:M${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } });
    currentRow++;
  }

  //console.log(`✅ ENTERING/LEAVING AIR STATUS 섹션 완료: ${ahu.ahuNo || ahu.id}`);

  return currentRow;
};
